import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Calendar, Tags, AlertTriangle, MessageSquare, CheckCircle, RotateCcw, GitBranch, ChevronDown, DollarSign, X, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { IntegrationChoiceDialog } from "@/components/conversations/IntegrationChoiceDialog";
import {
  fetchMemberAccessibleWhatsAppIntegrations,
  type MessagingIntegrationOption,
} from "@/lib/member-accessible-integrations";
import { startLeadConversation } from "@/lib/start-lead-conversation";
import { EscalationHistory } from "./EscalationHistory";
import { LeadTagSelector } from "./LeadTagSelector";
import { LeadInstagramComments } from "./LeadInstagramComments";
import { LeadGroupSelector } from "./LeadGroupSelector";
import { LeadCommercialSection } from "./LeadCommercialSection";
import { LeadAppointmentHistory } from "./LeadAppointmentHistory";
import { LeadDealsSection } from "./LeadDealsSection";
import { LeadScheduledMessages } from "./LeadScheduledMessages";
import { LeadMediaLinksDocs } from "./LeadMediaLinksDocs";
import { useLeadCommercialProfile } from "@/hooks/useLeadCommercialProfile";
import { useDeals } from "@/hooks/useDeals";
import { useDealStages } from "@/hooks/useDealStages";
import { DealDialog } from "@/components/deals/DealDialog";
import { DealDetailDrawer } from "@/components/deals/DealDetailDrawer";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import type { Lead, Stage, SalesFunnel, LeadFunnelMembership } from "@/types/lead";
import type { DealWithStage } from "@/types/deal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { maskDocument, detectDocumentType, type DocumentType } from "@/lib/document-mask";
import { resolveFunnelDisplayName } from "@/lib/funnel-display-label";
import { LeadCustomFieldsInputs } from "@/components/leads/LeadCustomFieldsInputs";
import { customFieldRowsFromMetadata, mergeCustomFieldMetadata, type LeadCustomFieldRow } from "@/lib/partner-api";
import {
  normalizeStageSlug,
  pickPrimaryLeadPlacement,
  sortMembershipsPrimaryFirst,
} from "@/lib/lead-funnel-placement";
import { saveLeadsReturnContext } from "@/lib/leads-navigation-context";
import { useWorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";
import { LeadMemberAssignField } from "@/components/leads/LeadMemberAssignField";
import { fetchLeadAssigneeInfoByLeadIds } from "@/lib/lead-member-assignments";

const InstagramIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

interface Agent {
  id: string;
  name: string;
}

interface LeadDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead | null;
  workspaceId: string;
  onUpdate: () => void;
  activeFunnelId?: string | null;
  conversationId?: string | null;
  memberId?: string | null;
  onEscalationResolved?: (result: import("@/lib/resolve-escalation-followup").ResolveEscalationResult) => void | Promise<void>;
}

export function LeadDetailDrawer({
  open,
  onOpenChange,
  lead,
  workspaceId,
  onUpdate,
  activeFunnelId,
  conversationId,
  memberId: memberIdProp,
  onEscalationResolved,
}: LeadDetailDrawerProps) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [assignedMemberIds, setAssignedMemberIds] = useState<string[]>([]);
  const [customFieldRows, setCustomFieldRows] = useState<LeadCustomFieldRow[]>([]);
  const [stages, setStages] = useState<(Stage & { funnel_name?: string })[]>([]);
  const [funnels, setFunnels] = useState<SalesFunnel[]>([]);
  const [funnelMemberships, setFunnelMemberships] = useState<Array<LeadFunnelMembership & { funnel_name?: string }>>([]);
  const [selectedMoveFunnelId, setSelectedMoveFunnelId] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationState, setConversationState] = useState<string | null>(null);
  const [conversationIsClosed, setConversationIsClosed] = useState(false);
  const [isTogglingState, setIsTogglingState] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const integrationById = useWorkspaceIntegrationsMap(workspaceId);

  // Commercial profile
  const { summary, deals: commercialDeals, interests, refetch: refetchCommercial } = useLeadCommercialProfile(open && lead ? lead.id : null);
  const { stages: dealStages } = useDealStages(workspaceId);
  const { deals: allDeals, moveDealStage, updateDeal, deleteDeal } = useDeals(workspaceId);
  const { memberId } = useMemberPermissions();
  const [commercialOpen, setCommercialOpen] = useState(true);
  const [dealDialogOpen, setDealDialogOpen] = useState(false);
  const [selectedDealForDrawer, setSelectedDealForDrawer] = useState<DealWithStage | null>(null);
  const [dealDrawerOpen, setDealDrawerOpen] = useState(false);
  const [integrationChoiceOpen, setIntegrationChoiceOpen] = useState(false);
  const [integrationChoiceOptions, setIntegrationChoiceOptions] = useState<MessagingIntegrationOption[]>([]);
  const [integrationChoiceMode, setIntegrationChoiceMode] = useState<"open" | "start">("start");
  const [pendingConversationChoices, setPendingConversationChoices] = useState<
    Array<{ id: string; integration_id: string | null }>
  >([]);
  const [isGoingToConversation, setIsGoingToConversation] = useState(false);
  const [leadTab, setLeadTab] = useState("crm");
  const { user } = useAuth();

  const saveLeadReturnContext = () => {
    if (!lead) return;

    const placement = pickPrimaryLeadPlacement({
      lead: { funnel_id: lead.funnel_id, stage: lead.stage },
      memberships: funnelMemberships.map((m) => ({
        funnel_id: m.funnel_id,
        stage: m.stage,
        order_index: m.order_index,
      })),
      funnels: funnels.map((f) => ({
        id: f.id,
        name: f.name,
        color: f.color,
        integration_id: f.integration_id,
        is_default: f.is_default,
      })),
      preferIntegrationId: null,
    });
    const membershipInActiveFunnel = activeFunnelId
      ? funnelMemberships.find((m) => m.funnel_id === activeFunnelId)
      : null;

    if (activeFunnelId) {
      saveLeadsReturnContext({
        workspaceId,
        funnelId: activeFunnelId,
        stageSlug: membershipInActiveFunnel?.stage ?? lead.stage ?? null,
      });
    } else if (placement) {
      saveLeadsReturnContext({
        workspaceId,
        funnelId: placement.funnelId,
        stageSlug: placement.stageSlug,
      });
    } else if (lead.funnel_id) {
      saveLeadsReturnContext({
        workspaceId,
        funnelId: lead.funnel_id,
        stageSlug: lead.stage ?? null,
      });
    }
  };

  const openConversationById = (conversationId: string) => {
    onOpenChange(false);
    navigate(`/conversations?conversation_id=${conversationId}`);
  };

  const startConversationWithIntegration = async (integrationId: string) => {
    if (!lead) return;

    const result = await startLeadConversation({
      workspaceId,
      leadId: lead.id,
      channel: "whatsapp",
      integrationId,
    });

    if (!result.ok) {
      toast({
        title: "Erro ao iniciar conversa",
        description: result.error,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: result.isNew ? "Nova conversa criada" : "Conversa existente",
      description: result.isNew
        ? `Conversa iniciada com ${lead.name}`
        : `Abrindo conversa com ${lead.name}`,
    });
    openConversationById(result.conversation.id);
  };

  const handleIntegrationChoiceConfirm = async (integrationId: string) => {
    setIsGoingToConversation(true);
    try {
      if (integrationChoiceMode === "open") {
        const match = pendingConversationChoices.find(
          (conversation) => conversation.integration_id === integrationId,
        );
        if (!match) {
          toast({
            title: "Conversa não encontrada",
            description: "Não foi possível localizar a conversa para esta conexão.",
            variant: "destructive",
          });
          return;
        }

        setIntegrationChoiceOpen(false);
        openConversationById(match.id);
        return;
      }

      await startConversationWithIntegration(integrationId);
      setIntegrationChoiceOpen(false);
    } finally {
      setIsGoingToConversation(false);
    }
  };

  const handleGoToConversation = async () => {
    if (!lead || !user?.id) return;

    if (!lead.phone) {
      toast({
        title: "Lead sem telefone",
        description: "Cadastre um telefone para iniciar conversa no WhatsApp.",
        variant: "destructive",
      });
      return;
    }

    setIsGoingToConversation(true);
    saveLeadReturnContext();

    try {
      const accessibleIntegrations = await fetchMemberAccessibleWhatsAppIntegrations(
        workspaceId,
        user.id,
      );

      if (accessibleIntegrations.length === 0) {
        toast({
          title: "Nenhuma conexão disponível",
          description: "Você não tem acesso a nenhuma conexão WhatsApp ativa.",
          variant: "destructive",
        });
        return;
      }

      const accessibleIntegrationIds = new Set(accessibleIntegrations.map((item) => item.id));
      const { data: conversations } = await supabase
        .from("conversations")
        .select("id, integration_id, updated_at")
        .eq("lead_id", lead.id)
        .eq("workspace_id", workspaceId)
        .eq("channel", "whatsapp")
        .order("updated_at", { ascending: false });

      const accessibleConversations = (conversations ?? []).filter((conversation) => {
        if (!conversation.integration_id) return true;
        return accessibleIntegrationIds.has(conversation.integration_id);
      });

      if (accessibleConversations.length === 1) {
        openConversationById(accessibleConversations[0].id);
        return;
      }

      if (accessibleConversations.length > 1) {
        const integrationIdsInUse = new Set(
          accessibleConversations
            .map((conversation) => conversation.integration_id)
            .filter((integrationId): integrationId is string => Boolean(integrationId)),
        );
        const openChoices = accessibleIntegrations.filter((integration) =>
          integrationIdsInUse.has(integration.id),
        );

        if (openChoices.length <= 1) {
          openConversationById(accessibleConversations[0].id);
          return;
        }

        setIntegrationChoiceMode("open");
        setPendingConversationChoices(accessibleConversations);
        setIntegrationChoiceOptions(openChoices);
        setIntegrationChoiceOpen(true);
        return;
      }

      if (accessibleIntegrations.length === 1) {
        await startConversationWithIntegration(accessibleIntegrations[0].id);
        return;
      }

      setIntegrationChoiceMode("start");
      setPendingConversationChoices([]);
      setIntegrationChoiceOptions(accessibleIntegrations);
      setIntegrationChoiceOpen(true);
    } catch (error) {
      console.error("handleGoToConversation error:", error);
      toast({
        title: "Erro ao abrir conversa",
        description: "Não foi possível iniciar a conversa com este lead.",
        variant: "destructive",
      });
    } finally {
      setIsGoingToConversation(false);
    }
  };

  // Dynamic form schema based on available stages
  const formSchema = z.object({
    name: z.string().min(1, "Nome é obrigatório"),
    email: z.string().email("Email inválido").optional().or(z.literal("")),
    phone: z.string().optional(),
    stage: z.string().min(1, "Estágio é obrigatório"),
    notes: z.string().optional(),
    assigned_agent_id: z.string().optional(),
    razao_social: z.string().optional(),
    document_type: z.enum(["cpf", "cnpj"]).default("cnpj"),
    document: z.string().optional(),
    cidade_estado: z.string().optional(),
    cep: z.string().optional(),
    endereco: z.string().optional(),
    numero: z.string().optional(),
    complemento: z.string().optional(),
    bairro: z.string().optional(),
    cidade: z.string().optional(),
    uf: z.string().optional(),
    birthday: z.string().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      stage: "novo",
      notes: "",
      assigned_agent_id: "none",
      razao_social: "",
      document_type: "cnpj",
      document: "",
      cidade_estado: "",
      cep: "",
      endereco: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      uf: "",
      birthday: "",
    },
  });

  useEffect(() => {
    if (open && workspaceId) {
      fetchAgents();
      fetchStagesWithFunnels();
    }
  }, [open, workspaceId]);

  const fetchFunnelMemberships = async (leadId: string) => {
    const { data: memberships } = await supabase
      .from("lead_funnel_memberships")
      .select("id, lead_id, funnel_id, stage, order_index, created_at")
      .eq("lead_id", leadId)
      .order("order_index", { ascending: true });

    // Fallback: se não há registros em lead_funnel_memberships, usar lead.funnel_id
    if (!memberships?.length) {
      if (lead?.funnel_id) {
        const { data: funnelRow } = await supabase
          .from("sales_funnels")
          .select("id, name, integration_id")
          .eq("id", lead.funnel_id)
          .maybeSingle();
        if (funnelRow) {
          setFunnelMemberships([{
            id: `virtual-${leadId}-${lead.funnel_id}`,
            lead_id: leadId,
            funnel_id: lead.funnel_id,
            stage: lead.stage || "novo",
            order_index: 0,
            created_at: lead.created_at,
            funnel_name: resolveFunnelDisplayName(funnelRow, integrationById),
          }]);
          return;
        }
      }
      setFunnelMemberships([]);
      return;
    }

    const funnelIds = [...new Set(memberships.map((m: any) => m.funnel_id))];
    const { data: funnelRows } = await supabase
      .from("sales_funnels")
      .select("id, name, integration_id, color, is_default")
      .in("id", funnelIds);
    const nameByFunnel = new Map(
      (funnelRows || []).map((f: any) => [
        f.id,
        resolveFunnelDisplayName(f, integrationById),
      ])
    );
    const enriched = (memberships || []).map((m: any) => ({
      ...m,
      funnel_name: nameByFunnel.get(m.funnel_id) || "Outro funil (sem acesso)",
    }));

    const primary = pickPrimaryLeadPlacement({
      lead: lead ?? { funnel_id: undefined, stage: "novo" },
      memberships: enriched,
      funnels: funnelRows || [],
    });

    setFunnelMemberships(
      sortMembershipsPrimaryFirst(enriched, primary?.funnelId),
    );
  };

  useEffect(() => {
    if (open && lead) fetchFunnelMemberships(lead.id);
    else setFunnelMemberships([]);
  }, [open, lead?.id]);

  // Fetch conversation state for the lead
  useEffect(() => {
    if (open && lead) {
      const fetchConversationState = async () => {
        const { data } = await supabase
          .from("conversations")
          .select("state, is_closed" as any)
          .eq("lead_id", lead.id)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        const convData = data as any;
        setConversationState(convData?.state || null);
        setConversationIsClosed(convData?.is_closed || false);
      };
      fetchConversationState();
    }
  }, [open, lead]);

  useEffect(() => {
    if (!open || !lead?.id) {
      setAssignedMemberIds([]);
      return;
    }

    let cancelled = false;
    const loadAssignees = async () => {
      const assigneeInfo = await fetchLeadAssigneeInfoByLeadIds(
        supabase,
        [lead.id],
        workspaceId,
      );

      if (cancelled) return;

      const info = assigneeInfo.get(lead.id);
      setAssignedMemberIds(info?.memberIds ?? []);
    };

    void loadAssignees();
    return () => {
      cancelled = true;
    };
  }, [open, lead?.id, lead?.assigned_member_id, workspaceId]);

  useEffect(() => {
    if (open) setLeadTab("crm");
  }, [open, lead?.id]);

  useEffect(() => {
    if (lead) {
      const meta = (lead.metadata as Record<string, any>) || {};
      const primary = pickPrimaryLeadPlacement({
        lead,
        memberships: funnelMemberships,
        funnels,
      });
      const effectiveFunnelId = primary?.funnelId || lead.funnel_id || "";
      const funnelStages = effectiveFunnelId
        ? stages.filter((s) => s.funnel_id === effectiveFunnelId)
        : [];
      const effectiveStage = normalizeStageSlug(
        primary?.stageSlug || lead.stage || "novo",
        funnelStages,
      );
      const existingDoc = meta.cpf || meta.cnpj || "";
      const existingDocType: DocumentType = meta.cpf ? "cpf" : (meta.cnpj ? detectDocumentType(meta.cnpj) : "cnpj");
      setSelectedMoveFunnelId(effectiveFunnelId || "");
      form.reset({
        name: lead.name,
        email: lead.email || "",
        phone: lead.phone || "",
        stage: effectiveStage,
        notes: lead.notes || "",
        assigned_agent_id: lead.assigned_agent_id || "none",
        razao_social: meta.razao_social || "",
        document_type: existingDocType,
        document: existingDoc ? maskDocument(existingDoc, existingDocType) : "",
        cidade_estado: meta.cidade_estado || "",
        cep: meta.cep || "",
        endereco: meta.endereco || "",
        numero: meta.numero || "",
        complemento: meta.complemento || "",
        bairro: meta.bairro || "",
        cidade: meta.cidade || "",
        uf: meta.uf || "",
        birthday: meta.birthday || "",
      });
    }
  }, [lead, form, funnelMemberships, stages, funnels]);

  useEffect(() => {
    if (!lead) {
      setCustomFieldRows([]);
      return;
    }
    setCustomFieldRows(customFieldRowsFromMetadata((lead.metadata as Record<string, unknown>) || {}));
  }, [lead?.id, lead?.updated_at]);

  const fetchAgents = async () => {
    const { data } = await supabase
      .from("agents")
      .select("id, name")
      .eq("workspace_id", workspaceId)
      .eq("is_active", true);

    if (data) setAgents(data);
  };

  const fetchStagesWithFunnels = async () => {
    // Fetch funnels and stages in parallel
    const [funnelsRes, stagesRes] = await Promise.all([
      supabase
        .from("sales_funnels")
        .select("id, workspace_id, name, color, is_default, order_index, created_at, integration_id")
        .eq("workspace_id", workspaceId)
        .order("order_index"),
      supabase
        .from("lead_stages")
        .select("id, name, slug, color, order_index, funnel_id")
        .eq("workspace_id", workspaceId)
        .order("order_index"),
    ]);

    const funnelsList = (funnelsRes.data || []) as SalesFunnel[];
    setFunnels(funnelsList);

    const funnelMap = new Map(
      funnelsList.map((f) => [f.id, resolveFunnelDisplayName(f, integrationById)])
    );
    const stagesWithFunnel = (stagesRes.data || []).map(s => ({
      ...s,
      funnel_name: funnelMap.get(s.funnel_id || "") || "Sem funil",
    })) as (Stage & { funnel_name?: string })[];
    setStages(stagesWithFunnel);
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!lead) return;

    try {
      const existingMetadata = (lead.metadata as Record<string, any>) || {};
      const docDigits = (values.document || "").replace(/\D+/g, "");
      const metadata: Record<string, any> = {
        ...existingMetadata,
        razao_social: values.razao_social || undefined,
        cidade_estado: values.cidade_estado || undefined,
        cep: values.cep || undefined,
        endereco: values.endereco || undefined,
        numero: values.numero || undefined,
        complemento: values.complemento || undefined,
        bairro: values.bairro || undefined,
        cidade: values.cidade || undefined,
        uf: values.uf ? values.uf.toUpperCase() : undefined,
        birthday: values.birthday || undefined,
        cpf: values.document_type === "cpf" && docDigits ? values.document : undefined,
        cnpj: values.document_type === "cnpj" && docDigits ? values.document : undefined,
      };
      // Limpa campo do tipo oposto
      if (values.document_type === "cpf") delete metadata.cnpj;
      if (values.document_type === "cnpj") delete metadata.cpf;
      Object.keys(metadata).forEach(key => {
        if (metadata[key] === undefined) delete metadata[key];
      });
      const mergedMetadata = mergeCustomFieldMetadata(metadata, customFieldRows);

      const storedFunnelId = selectedMoveFunnelId;
      const selectedStage = stages.find(s => s.slug === values.stage && (storedFunnelId ? s.funnel_id === storedFunnelId : true));
      const newFunnelId = selectedStage?.funnel_id || storedFunnelId || lead.funnel_id;

      // Cada funil tem sua própria coluna: compara com a coluna atual do lead NAQUELE funil.
      const currentStageInFunnel =
        newFunnelId && newFunnelId !== lead.funnel_id
          ? funnelMemberships.find(m => m.funnel_id === newFunnelId && !String(m.id).startsWith("virtual-"))?.stage ?? null
          : lead.stage;
      const stageChanged = !!values.stage && values.stage !== currentStageInFunnel;

      if (stageChanged) {
        // Funil principal: atualiza o lead. Outro funil: adiciona/move só naquele funil, sem tirar dos demais.
        const { error: rpcErr } = await supabase.rpc("move_lead_to_stage", {
          p_lead_id: lead.id,
          p_stage: values.stage,
          p_funnel_id: newFunnelId || null,
          p_order_index: null,
        });
        if (rpcErr) throw rpcErr;
        void fetchFunnelMemberships(lead.id);
      }

      const { error } = await supabase
        .from("leads")
        .update({
          name: values.name,
          email: values.email || null,
          phone: values.phone || null,
          notes: values.notes || null,
          assigned_agent_id: values.assigned_agent_id && values.assigned_agent_id !== "none" ? values.assigned_agent_id : null,
          metadata: mergedMetadata as unknown as Json,
        })
        .eq("id", lead.id);

      if (error) throw error;

      // Propagar agente responsável para conversa ativa
      const newAgentId =
        values.assigned_agent_id && values.assigned_agent_id !== "none"
          ? values.assigned_agent_id
          : null;
      const previousAgentId = lead.assigned_agent_id || null;
      if (newAgentId !== previousAgentId) {
        let previousCalendarId: string | null = null;
        let newCalendarId: string | null = null;
        const agentIdsToFetch = [previousAgentId, newAgentId].filter((id): id is string => Boolean(id));
        if (agentIdsToFetch.length > 0) {
          const { data: agentCalRows } = await supabase
            .from("agents")
            .select("id, google_calendar_sync_calendar_id")
            .in("id", agentIdsToFetch);
          if (agentCalRows) {
            if (previousAgentId) {
              previousCalendarId =
                agentCalRows.find((a) => a.id === previousAgentId)?.google_calendar_sync_calendar_id ?? null;
            }
            if (newAgentId) {
              newCalendarId =
                agentCalRows.find((a) => a.id === newAgentId)?.google_calendar_sync_calendar_id ?? null;
            }
          }
        }
        const shouldClearOfferedSlots = previousCalendarId !== newCalendarId;

        const { data: activeConversations } = await supabase
          .from("conversations")
          .select("id, metadata")
          .eq("lead_id", lead.id)
          .eq("state", "active")
          .order("updated_at", { ascending: false })
          .limit(5);

        if (activeConversations && activeConversations.length > 0) {
          for (const conv of activeConversations) {
            const updatePayload: {
              agent_id: string | null;
              agent_override: boolean;
              metadata?: Record<string, unknown>;
            } = {
              agent_id: newAgentId,
              agent_override: newAgentId ? true : false,
            };
            if (shouldClearOfferedSlots) {
              const metadata = { ...((conv.metadata as Record<string, unknown>) || {}) };
              delete metadata.last_offered_slots;
              updatePayload.metadata = metadata;
            }
            await supabase.from("conversations").update(updatePayload as never).eq("id", conv.id);
          }
        }
      }

      toast({
        title: "Lead atualizado",
        description: "As informações do lead foram atualizadas com sucesso.",
      });

      onUpdate();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar lead",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!lead) return;

    setIsDeleting(true);
    try {
      // FK CASCADE cuida de deletar conversas, mensagens e dados associados automaticamente
      const { error: leadError } = await supabase
        .from("leads")
        .delete()
        .eq("id", lead.id);

      if (leadError) throw leadError;

      toast({
        title: "Lead e conversas excluídos",
        description: "O lead e todas as conversas associadas foram removidos com sucesso.",
      });

      onUpdate();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Erro ao excluir",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleToggleConversationState = async () => {
    if (!lead) return;
    setIsTogglingState(true);
    const newIsClosed = !conversationIsClosed;
    try {
      const { error } = await supabase
        .from("conversations")
        .update({ is_closed: newIsClosed, updated_at: new Date().toISOString() } as any)
        .eq("lead_id", lead.id);
      if (error) throw error;
      setConversationIsClosed(newIsClosed);
      toast({
        title: newIsClosed ? "✅ Atendimento encerrado" : "✅ Atendimento reaberto",
        description: newIsClosed
          ? "Todas as conversas deste lead foram encerradas."
          : "Todas as conversas deste lead foram reabertas.",
      });
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsTogglingState(false);
    }
  };

  const selectedMoveFunnel = funnels.find(f => f.id === selectedMoveFunnelId);
  const selectedMoveFunnelStages = stages.filter(s => s.funnel_id === selectedMoveFunnelId);
  const watchedStage = form.watch("stage");
  const selectedMoveStage = selectedMoveFunnelStages.find(s => s.slug === watchedStage);
  const currentStage =
    selectedMoveStage ||
    stages.find(
      (s) =>
        s.slug === watchedStage &&
        (!selectedMoveFunnelId || s.funnel_id === selectedMoveFunnelId),
    );

  if (!lead) return null;

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        hideClose
        className={cn(
          "flex h-full max-h-[100dvh] w-full max-w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[540px] sm:gap-4 sm:p-6",
          "border-l bg-background shadow-lg"
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:border-0 sm:px-0 sm:pb-0 sm:pt-0">
          <SheetHeader className="min-w-0 flex-1 space-y-1 p-0 text-left sm:space-y-2 sm:pr-8">
            <SheetTitle className="text-base sm:text-lg">Detalhes do Lead</SheetTitle>
            <SheetDescription className="text-xs sm:text-sm">
              Visualize e edite as informações do lead
            </SheetDescription>
          </SheetHeader>
          <SheetClose asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-full sm:h-9 sm:w-9"
              aria-label="Fechar"
            >
              <X className="h-5 w-5 sm:h-4 sm:w-4" />
            </Button>
          </SheetClose>
        </div>

        <Tabs
          value={leadTab}
          onValueChange={setLeadTab}
          className="flex min-h-0 min-w-0 flex-1 flex-col"
        >
          <div className="shrink-0 border-b px-4 py-2 sm:border-border/60 sm:px-0">
            <TabsList className="grid h-9 w-full grid-cols-4">
              <TabsTrigger value="crm">CRM</TabsTrigger>
              <TabsTrigger value="perfil">Perfil</TabsTrigger>
              <TabsTrigger value="midias">Mídias</TabsTrigger>
              <TabsTrigger value="informacoes">Informações</TabsTrigger>
            </TabsList>
          </div>

          <Form {...form}>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 pb-4 pt-4 sm:px-0 sm:pb-0">
                <TabsContent
                  value="crm"
                  forceMount
                  className="mt-0 space-y-6 focus-visible:ring-0 data-[state=inactive]:hidden"
                >
          {/* Tags Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Tags className="w-4 h-4 text-muted-foreground" />
              <h3 className="font-semibold text-sm">Tags</h3>
            </div>
            <LeadTagSelector workspaceId={workspaceId} leadId={lead.id} />
          </div>

          <Separator />

          <div className="space-y-4">
            <FormField
              control={form.control}
              name="assigned_agent_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Agente Responsável</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um agente..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {agents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Define qual agente IA atende este lead nas conversas ativas.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>Membros responsáveis</FormLabel>
              <LeadMemberAssignField
                workspaceId={workspaceId}
                leadId={lead.id}
                assignedMemberIds={assignedMemberIds}
                onAssign={(memberIds) => {
                  setAssignedMemberIds(memberIds);
                  onUpdate();
                }}
              />
              <FormDescription>
                Você pode atribuir mais de um membro da equipe a este lead.
              </FormDescription>
            </FormItem>
          </div>

          <Separator />

            {/* Funil/Estágio editável: escolha em duas etapas para mover o lead */}
            <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-muted-foreground" />
                <h3 className="font-semibold text-sm">Funil / coluna</h3>
              </div>
              <div className="space-y-2">
                <FormLabel>Funil</FormLabel>
                <Select
                  value={selectedMoveFunnelId || undefined}
                  onValueChange={(funnelId) => {
                    setSelectedMoveFunnelId(funnelId);

                    const currentStageSlug = form.getValues("stage");
                    const membershipStage = funnelMemberships.find(m => m.funnel_id === funnelId)?.stage;
                    const nextStage =
                      stages.find(s => s.funnel_id === funnelId && s.slug === membershipStage) ||
                      stages.find(s => s.funnel_id === funnelId && s.slug === currentStageSlug) ||
                      stages.find(s => s.funnel_id === funnelId);

                    form.setValue("stage", nextStage?.slug || "", {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o funil" />
                  </SelectTrigger>
                  <SelectContent>
                    {funnels.map((funnel) => {
                      const funnelStages = stages.filter(s => s.funnel_id === funnel.id);
                      if (funnelStages.length === 0) return null;
                      return (
                        <SelectItem key={funnel.id} value={funnel.id}>
                          <div className="flex items-center gap-2">
                            <div className={cn("w-2 h-2 rounded-full shrink-0", funnel.color)} />
                            <span>{resolveFunnelDisplayName(funnel, integrationById)}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <FormField
                control={form.control}
                name="stage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Coluna</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || undefined}
                      disabled={!selectedMoveFunnelId || selectedMoveFunnelStages.length === 0}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={selectedMoveFunnel ? "Selecione a coluna" : "Selecione um funil primeiro"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {selectedMoveFunnelStages.map((stage) => (
                          <SelectItem key={stage.id} value={stage.slug}>
                            <div className="flex items-center gap-2">
                              <div className={cn("w-2 h-2 rounded-full shrink-0", stage.color)} />
                              <span>{stage.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Escolha o funil e a coluna e salve. Se o lead ainda não estiver nesse funil, ele é adicionado sem sair dos outros.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Onde o lead está em cada funil: editar seleciona o funil acima; remover tira de funil extra */}
              <div className="pt-2 border-t border-border/50">
                <p className="text-xs font-medium text-muted-foreground mb-2">Funis em que este lead está</p>
                {funnelMemberships.length > 0 ? (
                  <ul className="text-sm text-muted-foreground space-y-1.5">
                    {funnelMemberships.map((m) => {
                      const funnelStages = stages.filter((s) => s.funnel_id === m.funnel_id);
                      const resolvedSlug = normalizeStageSlug(m.stage, funnelStages);
                      const stageLabel =
                        funnelStages.find((s) => s.slug === resolvedSlug)?.name ?? m.stage;
                      const canEdit = funnels.some((f) => f.id === m.funnel_id);
                      const isPrimary = m.funnel_id === lead.funnel_id;
                      return (
                        <li key={m.id} className="flex items-center justify-between gap-2">
                          <button
                            type="button"
                            disabled={!canEdit}
                            className={cn("text-left truncate", canEdit && "hover:text-foreground underline-offset-2 hover:underline")}
                            onClick={() => {
                              setSelectedMoveFunnelId(m.funnel_id);
                              form.setValue("stage", resolvedSlug, { shouldDirty: false });
                            }}
                          >
                            {m.funnel_name ?? "Funil"}
                          </button>
                          <div className="flex items-center gap-1 shrink-0">
                            <Badge variant="secondary" className="text-xs font-normal">{stageLabel}</Badge>
                            {canEdit && !isPrimary && !String(m.id).startsWith("virtual-") && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                aria-label={`Remover do funil ${m.funnel_name ?? ""}`}
                                onClick={async () => {
                                  const { error } = await supabase.rpc("remove_lead_from_funnel", {
                                    p_lead_id: lead.id,
                                    p_funnel_id: m.funnel_id,
                                  });
                                  if (error) {
                                    toast({ title: "Não foi possível remover do funil", description: error.message, variant: "destructive" });
                                    return;
                                  }
                                  await fetchFunnelMemberships(lead.id);
                                  onUpdate();
                                }}
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhum funil ainda. Selecione acima e salve.</p>
                )}
              </div>
            </div>

          <Separator />

          {/* Groups/Companies Section */}
          <LeadGroupSelector workspaceId={workspaceId} leadId={lead.id} />

          <Separator />

          {/* Commercial Section */}
          <Collapsible open={commercialOpen} onOpenChange={setCommercialOpen}>
            <CollapsibleTrigger asChild>
              <Button type="button" variant="ghost" className="w-full justify-between px-0 hover:bg-transparent">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-muted-foreground" />
                  <h3 className="font-semibold text-sm">Comercial</h3>
                  {summary?.has_open_deal && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      {summary.open_deals_count} aberta{summary.open_deals_count !== 1 ? "s" : ""}
                    </Badge>
                  )}
                </div>
                <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", commercialOpen && "rotate-180")} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 pt-2">
              <LeadCommercialSection summary={summary} />
              <LeadDealsSection
                deals={commercialDeals}
                onNewDeal={() => setDealDialogOpen(true)}
                onDealClick={(deal) => {
                  const fullDeal = allDeals.find(d => d.id === deal.id);
                  if (fullDeal) {
                    setSelectedDealForDrawer(fullDeal);
                    setDealDrawerOpen(true);
                  }
                }}
              />
            </CollapsibleContent>
          </Collapsible>
                </TabsContent>

                <TabsContent
                  value="perfil"
                  forceMount
                  className="mt-0 space-y-4 focus-visible:ring-0 data-[state=inactive]:hidden"
                >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Mostrar telefone apenas se NÃO for lead de Instagram */}
              {lead.channel !== 'instagram' && (
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <LeadCustomFieldsInputs
                rows={customFieldRows}
                onChange={setCustomFieldRows}
              />

              <FormField
                control={form.control}
                name="razao_social"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Razão Social</FormLabel>
                    <FormControl>
                      <Input placeholder="Empresa LTDA" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="document_type"
                render={({ field }) => {
                  const docType = (field.value as DocumentType) || "cnpj";
                  return (
                    <FormItem>
                      <FormLabel>Documento</FormLabel>
                      <div className="flex flex-col gap-2">
                        <ToggleGroup
                          type="single"
                          value={docType}
                          onValueChange={(v) => {
                            if (!v) return;
                            field.onChange(v);
                            const current = form.getValues("document") || "";
                            form.setValue("document", maskDocument(current, v as DocumentType));
                          }}
                          className="justify-start"
                        >
                          <ToggleGroupItem value="cpf" aria-label="CPF" className="h-8 px-3 text-xs">CPF</ToggleGroupItem>
                          <ToggleGroupItem value="cnpj" aria-label="CNPJ" className="h-8 px-3 text-xs">CNPJ</ToggleGroupItem>
                        </ToggleGroup>
                        <FormField
                          control={form.control}
                          name="document"
                          render={({ field: docField }) => (
                            <FormControl>
                              <Input
                                placeholder={docType === "cpf" ? "000.000.000-00" : "00.000.000/0000-00"}
                                value={(docField.value as string) || ""}
                                onChange={(e) => docField.onChange(maskDocument(e.target.value, docType))}
                                inputMode="numeric"
                              />
                            </FormControl>
                          )}
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
                <h4 className="text-sm font-medium">Endereço</h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="cep"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>CEP</FormLabel>
                        <FormControl>
                          <Input placeholder="00000-000" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cidade"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cidade</FormLabel>
                        <FormControl>
                          <Input placeholder="São Paulo" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="uf"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>UF</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="SP"
                            maxLength={2}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_7rem]">
                  <FormField
                    control={form.control}
                    name="endereco"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Endereço</FormLabel>
                        <FormControl>
                          <Input placeholder="Rua, avenida..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="numero"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nº do endereço</FormLabel>
                        <FormControl>
                          <Input placeholder="123" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="complemento"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Complemento</FormLabel>
                        <FormControl>
                          <Input placeholder="Apto, sala..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="bairro"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Bairro</FormLabel>
                        <FormControl>
                          <Input placeholder="Bairro" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="cidade_estado"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cidade/Estado (legado)</FormLabel>
                      <FormControl>
                        <Input placeholder="São Paulo/SP" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="birthday"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de Nascimento</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notas</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={4}
                        placeholder="Adicione notas sobre este lead..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

                </TabsContent>

                <TabsContent
                  value="midias"
                  forceMount
                  className="mt-0 space-y-6 focus-visible:ring-0 data-[state=inactive]:hidden"
                >
                  <LeadMediaLinksDocs leadId={lead.id} variant="panel" />
                </TabsContent>

                <TabsContent
                  value="informacoes"
                  forceMount
                  className="mt-0 space-y-6 focus-visible:ring-0 data-[state=inactive]:hidden"
                >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Criado em {new Date(lead.created_at).toLocaleDateString("pt-BR")}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {currentStage && (
                <Badge className={currentStage.color + " text-white"}>
                  {currentStage.name}
                </Badge>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              className="w-full min-w-0 sm:flex-1"
              onClick={handleGoToConversation}
              disabled={isGoingToConversation}
            >
              {isGoingToConversation ? (
                <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <MessageSquare className="mr-2 h-4 w-4 shrink-0" />
              )}
              Ir para Conversa
            </Button>

            {conversationState && (
              <Button
                type="button"
                variant={conversationIsClosed ? "default" : "outline"}
                className="w-full min-w-0 shrink-0 sm:w-auto sm:min-w-[8.5rem]"
                onClick={handleToggleConversationState}
                disabled={isTogglingState}
              >
                {conversationIsClosed ? (
                  <>
                    <RotateCcw className="mr-1.5 h-4 w-4 shrink-0" />
                    Reabrir
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-1.5 h-4 w-4 shrink-0" />
                    Encerrar
                  </>
                )}
              </Button>
            )}
          </div>

          {conversationIsClosed && (
            <Badge variant="secondary" className="w-fit bg-muted text-muted-foreground">
              Atendimento Encerrado
            </Badge>
          )}

          {lead.channel === "instagram" && lead.metadata?.instagram_username && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <InstagramIcon className="w-4 h-4 text-pink-500" />
                  <h3 className="font-semibold text-sm">Instagram</h3>
                </div>
                <div className="flex items-center gap-3">
                  {lead.metadata?.instagram_profile_pic && (
                    <img
                      src={lead.metadata.instagram_profile_pic}
                      alt="Profile"
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <p className="font-medium">@{lead.metadata.instagram_username}</p>
                    {lead.metadata?.instagram_name && (
                      <p className="text-sm text-muted-foreground">{lead.metadata.instagram_name}</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          <Separator />
          <LeadInstagramComments leadId={lead.id} workspaceId={workspaceId} />

           <Separator />
           <LeadAppointmentHistory leadId={lead.id} workspaceId={workspaceId} />

           <Separator />
           <LeadScheduledMessages leadId={lead.id} />

          <Separator />
          <EscalationHistory
            leadId={lead.id}
            workspaceId={workspaceId}
            conversationId={conversationId}
            memberId={memberIdProp ?? memberId}
            onResolve={onUpdate}
            onEscalationResolved={onEscalationResolved}
          />
                </TabsContent>
              </div>

              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex shrink-0 gap-2 border-t bg-background px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-0 sm:pb-0"
              >
                <Button type="submit" className="flex-1">
                  Salvar Alterações
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </Form>
        </Tabs>

          {/* AlertDialog de confirmação */}
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent className="max-w-md">
              <AlertDialogHeader className="space-y-4">
                <AlertDialogTitle className="flex items-center gap-3 text-lg font-semibold">
                  <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500" />
                  </div>
                  Confirmar Exclusão
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-4">
                    <p className="text-sm">
                      <span className="text-muted-foreground">Excluir: </span>
                      <span className="font-semibold text-primary">{lead?.name}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Esta ação irá remover permanentemente:
                    </p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-muted-foreground/70">•</span>
                        O lead e todas as suas informações
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-muted-foreground/70">•</span>
                        Todas as conversas associadas
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-muted-foreground/70">•</span>
                        Tags e grupos vinculados
                      </li>
                    </ul>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="gap-2 sm:gap-0">
                <AlertDialogCancel className="border-0 bg-transparent hover:bg-muted">Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={confirmDelete} 
                  className="bg-destructive hover:bg-destructive/90"
                  disabled={isDeleting}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {isDeleting ? "Excluindo..." : "Excluir"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
      </SheetContent>
    </Sheet>

    <IntegrationChoiceDialog
      open={integrationChoiceOpen}
      onOpenChange={setIntegrationChoiceOpen}
      title={integrationChoiceMode === "open" ? "Abrir conversa" : "Iniciar conversa"}
      description={
        integrationChoiceMode === "open"
          ? "Este lead possui conversas em mais de uma conexão. Escolha qual deseja abrir."
          : "Selecione por qual número deseja iniciar a conversa com este lead."
      }
      confirmLabel={integrationChoiceMode === "open" ? "Abrir conversa" : "Iniciar conversa"}
      integrations={integrationChoiceOptions}
      loading={isGoingToConversation}
      onConfirm={handleIntegrationChoiceConfirm}
    />

    {/* Deal creation dialog for this lead */}
    {lead && (
      <DealDialog
        open={dealDialogOpen}
        onOpenChange={setDealDialogOpen}
        workspaceId={workspaceId}
        stages={dealStages}
        memberId={memberId}
        onCreated={() => refetchCommercial()}
        initialLeadId={lead.id}
        initialLeadName={lead.name}
        lockLead
      />
    )}

    {/* Deal detail drawer (reuses same component from /deals) */}
    <DealDetailDrawer
      open={dealDrawerOpen}
      onOpenChange={setDealDrawerOpen}
      deal={selectedDealForDrawer}
      stages={dealStages}
      workspaceId={workspaceId}
      memberId={memberId}
      onUpdate={(id, updates) => {
        updateDeal.mutate({ id, ...updates });
        refetchCommercial();
      }}
      onMoveDealStage={(dealId, toStageId, mid) => {
        moveDealStage.mutate({ dealId, toStageId, memberId: mid });
        refetchCommercial();
      }}
      onDelete={(id) => {
        deleteDeal.mutate(id);
        refetchCommercial();
      }}
    />
    </>
  );
}
